import { Checkbox, Form, Grid, Input, SingleDatePicker } from '@mairistem/composants';
import { FicheContainerContext } from '@mairistem/fiche';
import { isNewIdentifiant, request } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { getCivilites } from '@/routes';
import type { AdresseType, ApiResponseType, CiviliteType, ErrorFieldType } from '@/types';
import PayeurAdresseList from '../../PayeurAdresseList/PayeurAdresseList';
import PayeurContactList from '../../PayeurContactList/PayeurContactList';

interface PayeurPanelIdentificationProps {
  identifiant: string | number;
  errors?: ErrorFieldType[];
  civilite?: CiviliteType;
  adresse: AdresseType;
  nom: string;
  prenom: string;
  dateNaissance: string;
  paysNaissance?: string;
  villeNaissance?: string;
  departementNaissance?: string;
  mail: string;
  telephone: string;
  portable: string;
  disabled: boolean;
  onChange: (e: any, shouldChangeStatus?: boolean) => void;
}

const PayeurPanelIdentification = (props: PayeurPanelIdentificationProps) => {
  const {
    identifiant,
    errors,
    civilite,
    adresse,
    nom = '',
    prenom = '',
    dateNaissance,
    paysNaissance = '',
    villeNaissance = '',
    departementNaissance = '',
    mail = '',
    telephone = '',
    portable = '',
    disabled,
    onChange,
  } = props;

  const { status } = React.useContext(FicheContainerContext);
  const { created } = status || {};
  const [civilites, setCivilites] = React.useState<CiviliteType[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  const identifiantRef = React.useRef<string | number>();

  const handleChange = React.useCallback(
    ({ target }) => {
      const { name, value } = target;

      if (onChange) {
        onChange({
          identifiant,
          [name]: value,
        });
      }
    },
    [identifiant, onChange],
  );

  const handlePaysCheckboxChange = React.useCallback(
    (e, { checked }) => {
      if (checked) {
        onChange({
          identifiant,
          paysNaissance: 'France',
          villeNaissance: '',
          departementNaissance: '',
        });
      } else {
        onChange({
          identifiant,
          paysNaissance: '',
          villeNaissance: '',
          departementNaissance: '',
        });
      }
    },
    [identifiant, onChange],
  );

  const handleChangeCivilite = React.useCallback(
    (e, { value }) => {
      const civ = _.find(civilites, { identifiant: value });
      if (onChange && civ) {
        onChange({
          identifiant,
          civilite: civ,
        });
      }
    },
    [civilites, identifiant, onChange],
  );

  const handleChangeCollection = React.useCallback(
    (data) => {
      if (onChange) {
        onChange({
          identifiant,
          ...data,
        });
      }
    },
    [identifiant, onChange],
  );

  React.useEffect(() => {
    const pendingPromises = [];
    setLoading(true);

    request
      .get(getCivilites())
      .then(({ data, meta: { lastPage } }: ApiResponseType<CiviliteType>) => {
        for (let i = 2; i <= lastPage; i += 1) {
          pendingPromises.push(request.get(getCivilites({ page: i })));
        }

        Promise.all(pendingPromises)
          .then((datas) => {
            let dataFormatted: CiviliteType[] = data;

            _.forEach(datas, ({ data: d }) => {
              dataFormatted = [...dataFormatted, ...d];
            });

            setCivilites(dataFormatted);
          })
          .finally(() => {
            setLoading(false);
          });
      });
  }, []);

  React.useEffect(() => {
    if (_.isNil(identifiantRef.current) && isNewIdentifiant(identifiant)) {
      onChange?.(
        {
          identifiant,
        },
        false,
      );
    }

    identifiantRef.current = identifiant;
  }, [created, identifiant, onChange]);

  /** Initialise les valeurs par défaut en création */
  React.useEffect(() => {
    // Si on arrive direct en création sur la fiche
    // par le speeddial par exemple, PayeurFicheBar initialise pas les valeurs par défaut
    // donc on doit le faire là
    if (_.isNil(identifiantRef.current) && isNewIdentifiant(identifiant)) {
      onChange?.(
        {
          identifiant,
          civilite: { identifiant: null },
          paysNaissance: 'France',
        },
        true,
      );
    }

    identifiantRef.current = identifiant;
  }, [created, identifiant, onChange]);

  const optionsCivilites = React.useMemo(
    () =>
      _.map(civilites, ({ identifiant: id, libelle }) => ({
        key: id,
        value: id,
        text: libelle,
      })),
    [civilites],
  );

  return (
    <Grid as={Form} stackable>
      <Grid.Row columns={2}>
        <Grid.Column>
          <Form.Group width="equal">
            <Form.Select
              data-testid="identification-input-civilite"
              required
              id="civilite"
              label="Civilité"
              name="identifiant"
              value={civilite?.identifiant}
              options={optionsCivilites}
              placeholder="Civilité"
              onChange={handleChangeCivilite}
              width={16}
              loading={loading}
              disabled={disabled}
              error={_.some(errors, { id: 'civilite' })}
            />
            <Form.Field
              input={{
                className: 'uppercase',
                'data-testid': 'identification-input-nom',
              }}
              required
              id="nom"
              label="Nom"
              name="nom"
              value={nom}
              error={_.some(errors, { id: 'nom' })}
              width={16}
              disabled={disabled}
              // @ts-expect-error
              control={Input}
              fluid
              onChange={handleChange}
            />
            <Form.Field
              required
              input={{
                className: 'capitalize',
                'data-testid': 'identification-input-prenom',
              }}
              id="prenom"
              label="Prénom"
              name="prenom"
              value={prenom}
              fluid
              width={16}
              onChange={handleChange}
              disabled={disabled}
              error={_.some(errors, { id: 'prenom' })}
              // @ts-expect-error
              control={Input}
            />
          </Form.Group>
          <Form.Group
            // props error pas géré sur le Form.Field mais la classe ici marche
            className={_.some(errors, { id: 'dateNaissance' }) ? 'error' : ''}
          >
            <Form.Field disabled={disabled}>
              <label>Date de naissance</label>
              <SingleDatePicker
                data-testid="identification-input-dateNaissance"
                // id="dateNaissance"
                clearable
                value={dateNaissance ? dayjs(dateNaissance) : null}
                onChange={(date: dayjs.Dayjs) => {
                  handleChange({
                    target: {
                      name: 'dateNaissance',
                      value: date?.format('YYYY-MM-DD'),
                    },
                  });
                }}
                disabled={disabled}
              />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field disabled={disabled}>
              <label>Lieu de naissance</label>
              <Checkbox
                label="Né en France"
                data-testid="identification-checkbox-pays-naissance"
                disabled={disabled}
                checked={paysNaissance === 'France'}
                onChange={handlePaysCheckboxChange}
              />
            </Form.Field>
            {paysNaissance === 'France' ? (
              <>
                <Form.Field
                  data-testid="identification-input-villeNaissance"
                  id="villeNaissance"
                  name="villeNaissance"
                  label="&nbsp;"
                  placeholder="Ville"
                  value={villeNaissance}
                  fluid
                  width={6}
                  onChange={handleChange}
                  disabled={disabled}
                  error={_.some(errors, { id: 'villeNaissance' })}
                  // @ts-expect-error
                  control={Input}
                />
                <Form.Field
                  input={{ 'data-testid': 'identification-input-departementNaissance' }}
                  id="departementNaissance"
                  name="departementNaissance"
                  label="&nbsp;"
                  placeholder="Département"
                  value={departementNaissance}
                  fluid
                  width={6}
                  onChange={handleChange}
                  disabled={disabled}
                  error={_.some(errors, { id: 'departementNaissance' })}
                  // @ts-expect-error
                  control={Input}
                />
              </>
            ) : (
              <Form.Field
                input={{ 'data-testid': 'identification-input-pays-naissance' }}
                id="paysNaissance"
                name="paysNaissance"
                label="&nbsp;"
                placeholder="Pays"
                value={paysNaissance}
                fluid
                width={6}
                onChange={handleChange}
                disabled={disabled}
                error={_.some(errors, { id: 'paysNaissance' })}
                // @ts-expect-error
                control={Input}
              />
            )}
          </Form.Group>
        </Grid.Column>
        <Grid.Column>
          <PayeurAdresseList
            adresse={adresse}
            onChange={handleChangeCollection}
            disabled={disabled}
          />
          <PayeurContactList
            telephone={telephone}
            portable={portable}
            mail={mail}
            onChange={handleChangeCollection}
            disabled={disabled}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default PayeurPanelIdentification;
